import { UIKit } from "@/shared/ui";
/**
 * Поле точного ввода длины стены.
 * Всплывает рядом с курсором, как только пользователь начал печатать цифры,
 * и строит стену ровно указанной длины в текущем направлении.
 * У прямоугольника — два числа через пробел или «x»: ширина и длина комнаты.
 * Число — в единице редактора (мм, см, м, дюймы): её подпись стоит после поля.
 */
import { useEffect, useRef } from "react";
import { LENGTH_UNITS, formatAngle } from "@/shared/lib/geometry";
import { useLengthUnit } from "@/shared/store";
import type { ScreenPoint } from "../hooks/useDrawWall";

interface Props {
	value: string;
	/** Позиция курсора внутри холста, px */
	at: ScreenPoint | null;
	/** Текущее направление линии */
	angle: number;
	onChange: (value: string) => void;
	onCommit: () => void;
	onCancel: () => void;
	/** Два размера — ширина и длина прямоугольника, а не одна длина */
	pair?: boolean;
}

export const LengthInput = ({ value, at, angle, onChange, onCommit, onCancel, pair }: Props) => {
	const ref = useRef<HTMLInputElement>(null);
	const unit = useLengthUnit();

	useEffect(() => {
		ref.current?.focus();
		ref.current?.setSelectionRange(value.length, value.length);
		// фокус ставим один раз при появлении поля
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div
			className="absolute z-5 flex items-center gap-1 rounded-lg border border-accent bg-surface px-2 py-1 shadow-floating"
			style={{ left: (at?.x ?? 24) + 18, top: (at?.y ?? 24) + 18 }}
		>
			<UIKit.NumberInput
				bare
				inputRef={ref}
				inputClassName={`${pair ? "w-28" : "w-19"} border-none bg-transparent text-right text-sm text-ink tabular-nums outline-none`}
				value={value}
				type="text"
				inputMode={pair ? "text" : unit === "mm" ? "numeric" : "decimal"}
				onChange={(nextValue) =>
					onChange(nextValue.replace(pair ? /[^0-9.,\sx×*хХ]/gu : /[^0-9.,]/g, ""))
				}
				onKeyDown={(event) => {
					if (event.key === "Enter") {
						event.preventDefault();
						onCommit();
					} else if (event.key === "Escape") {
						event.preventDefault();
						event.stopPropagation();
						onCancel();
					}
				}}
			/>
			<UIKit.Text as="span" className="text-xs text-ink">
				{LENGTH_UNITS[unit].suffix}
			</UIKit.Text>
			{!pair && (
				<UIKit.Text
					as="span"
					className="ml-0.5 border-l border-border pl-2 text-xs text-accent tabular-nums"
				>
					{formatAngle(angle)}
				</UIKit.Text>
			)}
		</div>
	);
};
